import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "slide", "dot"]
  static values = { trackUrl: String }

  connect() {
    const today = new Date().toISOString().slice(0, 10)
    const dismissed = localStorage.getItem("popup_dismissed_date")
    if (dismissed === today) return

    setTimeout(() => {
      this.element.classList.remove("hidden")
      this.currentIndex = 0
      this._startAutoSlide()
      this._trackViews()
    }, 500)
    this._bindTouchEvents()
  }

  disconnect() {
    this._stopAutoSlide()
  }

  dismissToday() {
    const today = new Date().toISOString().slice(0, 10)
    localStorage.setItem("popup_dismissed_date", today)
    this.close()
  }

  close() {
    this._stopAutoSlide()
    this.element.classList.add("hidden")
  }

  goToSlide(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this._goTo(index)
  }

  prev() {
    const count = this.slideTargets.length
    const prev = (this.currentIndex - 1 + count) % count
    this._goTo(prev)
  }

  next() {
    const count = this.slideTargets.length
    const next = (this.currentIndex + 1) % count
    this._goTo(next)
  }

  trackClick(event) {
    const bannerId = event.currentTarget.dataset.bannerId
    if (!bannerId || !this.hasTrackUrlValue) return
    this._sendTrack({ id: bannerId, type: "click" })
  }

  _trackViews() {
    if (!this.hasTrackUrlValue || !this.hasSlideTarget) return
    const ids = this.slideTargets.map(s => s.dataset.bannerId).filter(Boolean)
    if (ids.length === 0) return

    const key = "popup_viewed_" + ids.sort().join(",")
    if (sessionStorage.getItem(key)) return
    sessionStorage.setItem(key, "1")

    this._sendTrack({ ids: ids, type: "view" })
  }

  _sendTrack(params) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new URLSearchParams()
    body.append("type", params.type)
    if (params.ids) {
      params.ids.forEach(id => body.append("ids[]", id))
    } else if (params.id) {
      body.append("id", params.id)
    }
    fetch(this.trackUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken || ""
      },
      body: body.toString()
    }).catch(() => {})  // fire and forget
  }

  _goTo(index) {
    this.currentIndex = index
    const track = this.element.querySelector(".popup-track")
    if (track) {
      track.style.transform = `translateX(-${index * 100}%)`
    }
    if (this.hasDotTarget) {
      this.dotTargets.forEach((dot, i) => {
        dot.classList.toggle("active", i === index)
      })
    }
  }

  _startAutoSlide() {
    const count = this.slideTargets.length
    if (count <= 1) return
    this._timer = setInterval(() => {
      const next = (this.currentIndex + 1) % count
      this._goTo(next)
    }, 4500)
  }

  _stopAutoSlide() {
    if (this._timer) {
      clearInterval(this._timer)
      this._timer = null
    }
  }

  _bindTouchEvents() {
    const track = this.element.querySelector(".popup-track")
    if (!track) return
    track.addEventListener("touchstart", (e) => this._onTouchStart(e), { passive: true })
    track.addEventListener("touchend", (e) => this._onTouchEnd(e), { passive: true })
  }

  _onTouchStart(e) {
    this._touchStartX = e.changedTouches[0].clientX
    this._stopAutoSlide()
  }

  _onTouchEnd(e) {
    if (this._touchStartX === undefined) return
    const deltaX = e.changedTouches[0].clientX - this._touchStartX
    this._touchStartX = undefined
    if (Math.abs(deltaX) < 50) return
    if (deltaX < 0) {
      this.next()
    } else {
      this.prev()
    }
    this._startAutoSlide()
  }
}
